<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { RouteLocationPathRaw, RouterLink } from 'vue-router';
import { useStore } from 'vuex';

import ThemedButton from '@/components/base/ThemedButton.vue';
import ToastNotification from '@/components/base/ToastNotification.vue';
import BaseBodyText from '@/components/base/typography/BaseBodyText.vue';
import Header5 from '@/components/base/typography/Header5.vue';
import { useBundleCustomizer } from '@/composables/useBundleCustomizer';
import { useCallback } from '@/composables/useCallback';
import { useCart } from '@/composables/useCart';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { decodeCustomizerSelections } from '@/lib/customizers/common';
import { CUSTOM_MIX_5LB_SKU } from '@/lib/customizers/mixes';
import { MEDIUM_CUSTOM_TRAY_SKU } from '@/lib/customizers/trays';
import { CustomizerRecommendation } from '@/utils/cms';

const { recommendation, type } = defineProps<{
  recommendation: CustomizerRecommendation;
  type: 'CSB' | 'Custom Mix' | 'Custom Tray';
}>();

const store = useStore();
const { addToCart, createCustomMix, createCustomTray } = useCart(store);
const { bundleParentLookup, loadBundleParentLookup } = useBundleCustomizer();
const { flags } = useFeatureFlags();

const notificationMessage = ref<string>();

const handleAddToCart = useCallback(async () => {
  if (recommendation.sku) {
    await addToCart(
      {
        cart_sku: {
          quantity: 1,
          sku_external_id: recommendation.sku,
        },
      },
      {
        analyticsContext: { pageSection: 'Product Recommendations' },
        postAddToCartCallback: () => {},
        sendAnalytics: true,
        skipCartAdded: false,
      },
    );
  } else if (type === 'Custom Mix') {
    const variantContents = Object.fromEntries(
      decodeCustomizerSelections(recommendation.code, true).map((product) => {
        const [key, parts] = product.split(':');
        return [key, parts];
      }),
    );

    await createCustomMix(CUSTOM_MIX_5LB_SKU, variantContents, {
      analyticsContext: { pageSection: 'Product Recommendations' },
      showCartAddedModal: true,
    });
  } else if (type === 'Custom Tray') {
    await createCustomTray(
      MEDIUM_CUSTOM_TRAY_SKU,
      decodeCustomizerSelections(recommendation.code, false),
      {
        analyticsContext: { pageSection: 'Product Recommendations' },
        showCartAddedModal: true,
      },
    );
  } else if (type === 'CSB') {
    await loadBundleParentLookup();
    if (bundleParentLookup.value) {
      await addToCart(
        {
          cart_sku: {
            dynamic_bundle_skus: recommendation.code.split('~'),
            quantity: 1,
            sku_external_id: bundleParentLookup.value.variant.sku,
          },
        },
        {
          analyticsContext: { pageSection: 'Product Recommendations' },
          postAddToCartCallback: () => {},
          sendAnalytics: true,
          skipCartAdded: false,
        },
      );
    }
  }
});

const customizeLink = computed<RouteLocationPathRaw>(() => {
  let link: string;

  if (type === 'Custom Mix') link = 'mixes';
  else if (type === 'Custom Tray') link = 'trays';
  else link = flags.goatCsb ? 'greatesthitsbox' : 'snackbox';

  const selectionsParamKey = type === 'CSB' ? 'skus' : 'code';

  return {
    path: `/custom/${link}/builder`,
    query: {
      [selectionsParamKey]: recommendation.code,
      name: recommendation.name,
    },
  };
});

watch(
  () => handleAddToCart.error,
  (error) => {
    if (error) {
      notificationMessage.value =
        'Unable to add this item to cart. Please try again or create your own.';
    }
  },
);
</script>

<template>
  <div
    class="flex flex-col flex-1 justify-between p-4 text-center bg-white rounded-lg border border-solid border-stone-200"
  >
    <div>
      <Header5 headerTag="h3">{{ recommendation.name }}</Header5>
      <img
        :src="recommendation.image"
        :alt="recommendation.name"
        class="object-contain my-3 h-44"
      />
      <BaseBodyText class="px-4">{{ recommendation.description }}</BaseBodyText>
      <BaseBodyText
        v-if="recommendation.displayPrice"
        class="flex gap-2 justify-center mt-2 font-semibold"
        data-test="strikethrough-price"
      >
        <span>${{ recommendation.displayPrice }} </span>
        <del v-if="recommendation.strikethroughPrice" class="text-neutral-500">
          ${{ recommendation.strikethroughPrice }}
        </del>
      </BaseBodyText>
    </div>
    <div class="flex flex-col gap-2 pt-3 mt-auto">
      <ThemedButton
        v-if="recommendation.enableAddToCart"
        class="w-full"
        data-test="add-to-cart-button"
        :isLoading="handleAddToCart.isPending"
        @click="handleAddToCart.execute()"
      >
        Add to Cart
      </ThemedButton>
      <RouterLink
        v-if="recommendation.enableCustomizeButton"
        :to="customizeLink"
        class="w-full hover:no-underline"
      >
        <ThemedButton tabindex="-1" theme="white" fullWidth data-test="customize-button">
          Customize
        </ThemedButton>
      </RouterLink>
    </div>
    <div class="flex justify-center items-center w-full">
      <ToastNotification :show="!!notificationMessage" @hide="notificationMessage = ''">
        <SmallBodyText>{{ notificationMessage }}</SmallBodyText>
      </ToastNotification>
    </div>
  </div>
</template>
